'use client';

/**
 * DEMO MODE banner — shows at the top of every page when
 * NEXT_PUBLIC_DEMO_MODE=true. Lets the viewer know the data is seeded
 * and provides shortcut buttons to switch demo users.
 */

import { Sparkles } from 'lucide-react';

import { visibleStaffPersonas, visibleSubscriberPersonas } from '@/lib/demo-simple';
import { cn } from '@/lib/utils';

interface DemoChip {
  email: string;
  label: string;
  staff: boolean;
}

// Persona chips are derived from lib/demo-simple so the banner, the login
// quick-pick and the RBAC allowlists never drift. In simple mode this yields
// exactly the Premium subscriber + the single 'Admin (all access)' chip;
// otherwise the full free/pro/premium + Hersh/Kieran/campaign cast.
const DEMO_USERS: DemoChip[] = [
  ...visibleSubscriberPersonas().map((p) => ({ email: p.email, label: p.bannerLabel, staff: false })),
  ...visibleStaffPersonas().map((p) => ({ email: p.email, label: p.bannerLabel, staff: true })),
];

export function DemoBanner({ enabled }: { enabled?: boolean } = {}) {
  // `enabled` is the source of truth — passed by the server layout so the
  // value isn't subject to NEXT_PUBLIC_* inlining quirks in the client bundle.
  // Fallback to process.env for back-compat / non-layout mountings.
  const demoMode = enabled ?? process.env.NEXT_PUBLIC_DEMO_MODE === 'true';
  if (!demoMode) return null;

  return (
    <div
      data-demo-banner
      className="bg-[var(--color-surface)] border-b border-[var(--color-hair)] text-[var(--color-text)] px-4 py-2 text-xs flex items-center gap-3 flex-wrap"
    >
      <div className="flex items-center gap-2 font-mono">
        <Sparkles size={14} className="text-[var(--color-ochre-2)]" />
        <span className="uppercase tracking-wider">Demo mode</span>
        <span className="opacity-70">·</span>
        <span className="opacity-90">Seeded data, no real subscribers</span>
      </div>
      <div className="ml-auto flex items-center gap-2 flex-wrap">
        <span className="opacity-70">Switch user:</span>
        {DEMO_USERS.map((u) => (
          <button
            key={u.email}
            onClick={async () => {
              try {
                const res = await fetch('/api/v1/demo/login', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ email: u.email }),
                });
                if (res.ok) {
                  // Staff personas land in the analyst console; subscribers stay
                  // on the current portal page.
                  if (u.staff) {
                    window.location.href = '/analyst';
                  } else {
                    window.location.reload();
                  }
                }
              } catch (e) {
                console.error('demo login failed', e);
              }
            }}
            className={cn(
              'px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-mono',
              'bg-[var(--color-steel)] text-[var(--color-canvas)] hover:bg-[var(--color-steel-2)]',
              'transition-colors',
            )}
            title={u.email}
          >
            {u.label}
          </button>
        ))}
      </div>
    </div>
  );
}
